import { useEffect, useState, useMemo, useRef } from 'react'
import { CalendarDays, Filter, TrendingUp, Landmark, Clock, DollarSign, BarChart3, Timer } from 'lucide-react'
import { useCalendar } from '@/hooks/useCalendar'
import type { CalendarEvent, CalendarEventType } from '@/lib/types'
import { clsx } from 'clsx'

// ── Colour + icon config per event type ─────────────────────────────────────

const EVENT_CONFIG: Record<CalendarEventType, {
  label: string
  color: string
  bg: string
  border: string
  dot: string
  icon: typeof CalendarDays
}> = {
  earnings: {
    label: 'Earnings',
    color: 'text-blue-light',
    bg: 'bg-blue/10',
    border: 'border-blue/30',
    dot: 'bg-blue',
    icon: BarChart3,
  },
  economic: {
    label: 'Economic',
    color: 'text-amber-400',
    bg: 'bg-amber-400/10',
    border: 'border-amber-400/30',
    dot: 'bg-amber-400',
    icon: TrendingUp,
  },
  fomc: {
    label: 'FOMC',
    color: 'text-rose-light',
    bg: 'bg-rose/10',
    border: 'border-rose/30',
    dot: 'bg-rose',
    icon: Landmark,
  },
  expiry: {
    label: 'Options Expiry',
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
    border: 'border-purple-400/30',
    dot: 'bg-purple-400',
    icon: Clock,
  },
  dividend: {
    label: 'Dividend',
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    border: 'border-emerald-400/30',
    dot: 'bg-emerald-400',
    icon: DollarSign,
  },
}

const IMPACT_STYLES: Record<string, string> = {
  High: 'badge-red',
  Medium: 'badge-amber',
  Low: 'badge-neutral',
}

// ── Countdown helpers ────────────────────────────────────────────────────────

/**
 * Parse a CalendarEvent into a concrete Date object.
 * Events only carry a date string (YYYY-MM-DD); we assume the event fires at
 * a reasonable default time for each type:
 *   FOMC / economic  → 14:00 ET  = 19:00 UTC
 *   earnings          → 21:00 UTC (after US market close)
 *   expiry / dividend → 14:30 UTC (US market open)
 */
function eventDateTime(event: CalendarEvent): Date {
  const timeByType: Record<CalendarEventType, string> = {
    fomc:     'T19:00:00Z',
    economic: 'T13:30:00Z',
    earnings: 'T20:00:00Z',
    expiry:   'T14:30:00Z',
    dividend: 'T14:30:00Z',
  }
  const suffix = timeByType[event.event_type] ?? 'T14:30:00Z'
  return new Date(event.date + suffix)
}

interface Countdown {
  days: number
  hours: number
  minutes: number
  seconds: number
  total: number   // total seconds remaining (negative = past)
}

function calcCountdown(target: Date, now: Date): Countdown {
  const total = Math.floor((target.getTime() - now.getTime()) / 1000)
  if (total <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, total }
  const days    = Math.floor(total / 86400)
  const hours   = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  return { days, hours, minutes, seconds, total }
}

function formatCountdown(cd: Countdown): string {
  if (cd.total <= 0) return 'NOW'
  if (cd.days > 0)   return `${cd.days}d ${cd.hours}h`
  if (cd.hours > 0)  return `${cd.hours}h ${cd.minutes}m ${cd.seconds.toString().padStart(2, '0')}s`
  return `${cd.minutes}m ${cd.seconds.toString().padStart(2, '0')}s`
}

// ── ImmientCountdown sub-component ───────────────────────────────────────────

interface ImminentCardProps {
  event: CalendarEvent
  now: Date
}

function ImminentCard({ event, now }: ImminentCardProps) {
  const cfg  = EVENT_CONFIG[event.event_type]
  const Icon = cfg.icon
  const target = eventDateTime(event)
  const cd   = calcCountdown(target, now)
  const isPast = cd.total <= 0

  return (
    <div className={clsx(
      'flex flex-col items-center gap-2 rounded-2xl border p-4 min-w-[10rem] flex-1',
      cfg.bg, cfg.border,
    )}>
      <div className={clsx('p-2 rounded-xl', cfg.bg)}>
        <Icon size={20} className={cfg.color} aria-hidden />
      </div>
      <p className="text-xs font-semibold text-text-secondary text-center leading-tight line-clamp-2">
        {event.name}
      </p>
      <p className={clsx(
        'text-xl font-mono font-bold tabular-nums tracking-tight',
        isPast ? 'text-text-muted' : cfg.color,
      )}>
        {formatCountdown(cd)}
      </p>
      {!isPast && (
        <p className="text-[10px] text-text-muted">
          {target.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
        </p>
      )}
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

function groupByDate(events: CalendarEvent[]): Record<string, CalendarEvent[]> {
  const groups: Record<string, CalendarEvent[]> = {}
  for (const e of events) {
    if (!groups[e.date]) groups[e.date] = []
    groups[e.date].push(e)
  }
  return groups
}

// ── Component ───────────────────────────────────────────────────────────────

export default function CalendarPage() {
  const { events, loading, error, fetchEvents } = useCalendar()
  const [days, setDays] = useState(14)
  const [activeFilters, setActiveFilters] = useState<Set<CalendarEventType>>(
    new Set(['earnings', 'economic', 'fomc', 'expiry', 'dividend']),
  )
  const [now, setNow] = useState<Date>(() => new Date())
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Live countdown tick — updates every second
  useEffect(() => {
    intervalRef.current = setInterval(() => setNow(new Date()), 1000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  useEffect(() => {
    fetchEvents(days)
  }, [days, fetchEvents])

  const toggleFilter = (type: CalendarEventType) => {
    setActiveFilters(prev => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  const filtered = useMemo(
    () => events.filter(e => activeFilters.has(e.event_type)),
    [events, activeFilters],
  )

  const grouped = useMemo(() => groupByDate(filtered), [filtered])
  const sortedDates = useMemo(
    () => Object.keys(grouped).sort(),
    [grouped],
  )

  // Summary counts
  const counts = useMemo(() => {
    const c: Record<CalendarEventType, number> = {
      earnings: 0, economic: 0, fomc: 0, expiry: 0, dividend: 0,
    }
    for (const e of events) {
      c[e.event_type] = (c[e.event_type] || 0) + 1
    }
    return c
  }, [events])

  const highImpactCount = useMemo(
    () => events.filter(e => e.impact === 'High').length,
    [events],
  )

  // 3 most imminent future events for the countdown banner
  const imminentEvents = useMemo(() => {
    const nowMs = now.getTime()
    return [...events]
      .filter(e => eventDateTime(e).getTime() > nowMs - 3600_000) // allow 1h grace for "just passed"
      .sort((a, b) => eventDateTime(a).getTime() - eventDateTime(b).getTime())
      .slice(0, 3)
  }, [events, now])

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <CalendarDays size={24} className="text-blue" aria-hidden />
            Events Calendar
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Upcoming market-moving events for the next {days} days
          </p>
        </div>

        {/* Time range selector */}
        <div className="flex items-center gap-2">
          {[7, 14, 30].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={clsx(
                'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                days === d
                  ? 'bg-blue/20 text-blue-light'
                  : 'bg-surface-card text-text-secondary hover:bg-surface-card/80',
              )}
            >
              {d}D
            </button>
          ))}
        </div>
      </div>

      {/* ── Live countdown banner ── */}
      {imminentEvents.length > 0 && (
        <div className="glass rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Timer size={15} className="text-blue" aria-hidden />
            <span className="panel-title">
              Next up
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            {imminentEvents.map((event, idx) => (
              <ImminentCard
                key={`${event.date}-${event.event_type}-${idx}`}
                event={event}
                now={now}
              />
            ))}
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 stagger-children">
        <div className="card p-4">
          <p className="panel-title">Total Events</p>
          <p className="stat-hero text-text-primary mt-1">{events.length}</p>
        </div>
        <div className="card p-4">
          <p className="panel-title">High Impact</p>
          <p className="stat-value text-rose-light mt-1">{highImpactCount}</p>
        </div>
        {(Object.keys(EVENT_CONFIG) as CalendarEventType[]).slice(0, 4).map(type => (
          <div key={type} className="card p-4">
            <p className={clsx('panel-title', EVENT_CONFIG[type].color)}>{EVENT_CONFIG[type].label}</p>
            <p className="stat-value mt-1">{counts[type]}</p>
          </div>
        ))}
      </div>

      {/* Filter toggles */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={14} className="text-text-muted" aria-hidden />
        <span className="text-xs text-text-muted mr-1">Filter:</span>
        {(Object.keys(EVENT_CONFIG) as CalendarEventType[]).map(type => {
          const cfg = EVENT_CONFIG[type]
          const active = activeFilters.has(type)
          return (
            <button
              key={type}
              onClick={() => toggleFilter(type)}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border',
                active
                  ? `${cfg.bg} ${cfg.color} ${cfg.border}`
                  : 'bg-surface-card text-text-muted border-border opacity-50',
              )}
            >
              <span className={clsx('w-2 h-2 rounded-full', active ? cfg.dot : 'bg-text-muted')} />
              {cfg.label}
              <span className="opacity-60">({counts[type]})</span>
            </button>
          )
        })}
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 skeleton" />)}
        </div>
      )}
      {error && (
        <div className="bg-rose/10 border border-rose/30 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-sm text-rose-light flex-1">
            Failed to load calendar events: {error}
          </p>
          <button
            onClick={() => fetchEvents(days)}
            className="self-start sm:self-auto shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium bg-rose/20 text-rose-light border border-rose/30 hover:bg-rose/30 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Timeline */}
      {!loading && !error && (
        <div className="space-y-6">
          {sortedDates.length === 0 && (
            <div className="card p-8 text-center">
              <CalendarDays size={32} className="mx-auto text-text-muted mb-3" />
              <p className="text-text-muted text-sm">No events match your filters for this period.</p>
            </div>
          )}

          {sortedDates.map(dateStr => {
            const dayEvents = grouped[dateStr]
            const isToday = dateStr === new Date().toISOString().slice(0, 10)

            return (
              <div key={dateStr} className="relative">
                {/* Date header */}
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className={clsx(
                      'px-3 py-1 rounded-lg text-xs font-semibold',
                      isToday
                        ? 'bg-blue/20 text-blue-light'
                        : 'bg-surface-card text-text-secondary',
                    )}
                  >
                    {isToday ? 'Today' : formatDate(dateStr)}
                  </div>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-text-muted">
                    {dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Events for this date */}
                <div className="space-y-2 ml-2 border-l-2 border-border pl-4">
                  {dayEvents.map((event, idx) => {
                    const cfg = EVENT_CONFIG[event.event_type]
                    const Icon = cfg.icon

                    return (
                      <div
                        key={`${event.date}-${event.event_type}-${idx}`}
                        className={clsx(
                          'card hover-lift p-4',
                          cfg.border,
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          {/* Left: icon + content */}
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className={clsx('p-2 rounded-lg shrink-0', cfg.bg)}>
                              <Icon size={16} className={cfg.color} aria-hidden />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-text-primary truncate">
                                {event.name}
                              </p>
                              <p className="text-xs text-text-muted mt-0.5">
                                {event.description}
                              </p>

                              {/* Earnings-specific details */}
                              {event.event_type === 'earnings' && (
                                <div className="flex items-center gap-3 mt-2">
                                  {event.ticker && (
                                    <span className="text-xs font-mono bg-blue/10 text-blue-light px-2 py-0.5 rounded">
                                      {event.ticker}
                                    </span>
                                  )}
                                  {event.estimated_eps != null && (
                                    <span className="text-xs text-text-muted">
                                      Est. EPS: <span className="text-text-secondary font-medium">${event.estimated_eps.toFixed(2)}</span>
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Economic-specific details */}
                              {event.event_type === 'economic' && (
                                <div className="flex items-center gap-4 mt-2">
                                  {event.previous != null && (
                                    <span className="text-xs text-text-muted">
                                      Previous: <span className="text-text-secondary font-medium">{typeof event.previous === 'number' && event.previous > 1000 ? `${(event.previous / 1000).toFixed(0)}K` : event.previous}</span>
                                    </span>
                                  )}
                                  {event.consensus != null && (
                                    <span className="text-xs text-text-muted">
                                      Consensus: <span className="text-text-secondary font-medium">{typeof event.consensus === 'number' && event.consensus > 1000 ? `${(event.consensus / 1000).toFixed(0)}K` : event.consensus}</span>
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Dividend-specific details */}
                              {event.event_type === 'dividend' && (
                                <div className="flex items-center gap-3 mt-2">
                                  {event.ticker && (
                                    <span className="text-xs font-mono bg-emerald-400/10 text-emerald-400 px-2 py-0.5 rounded">
                                      {event.ticker}
                                    </span>
                                  )}
                                  {event.yield_pct != null && (
                                    <span className="text-xs text-text-muted">
                                      Yield: <span className="text-emerald-400 font-medium">{event.yield_pct.toFixed(2)}%</span>
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Expiry-specific details */}
                              {event.event_type === 'expiry' && event.expiry_type && (
                                <div className="mt-2">
                                  <span className={clsx(
                                    'text-xs px-2 py-0.5 rounded',
                                    event.expiry_type === 'monthly'
                                      ? 'bg-purple-400/10 text-purple-400'
                                      : 'bg-surface-DEFAULT text-text-muted',
                                  )}>
                                    {event.expiry_type === 'monthly' ? 'Monthly' : 'Weekly'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Right: impact badge + days */}
                          <div className="flex flex-col items-end gap-1.5 shrink-0">
                            <span className={IMPACT_STYLES[event.impact] || IMPACT_STYLES.Low}>
                              {event.impact}
                            </span>
                            {event.days_until != null && (
                              <span className="text-xs text-text-muted">
                                {event.days_until === 0
                                  ? 'Today'
                                  : event.days_until === 1
                                    ? 'Tomorrow'
                                    : `${event.days_until}d`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
